import React, { useMemo } from 'react';
import { AtbDetails } from 'src/components/AtbModal/AtbModal';
import { PriceSummary } from 'src/components/CustomisationPage/utils/priceEstimates';
import { shallowEqual, useSelector } from 'react-redux';
import { AppendState } from 'src/components/CustomisationPage/feature/customisation.actions';
import { AppendState as CartAppendState } from 'src/core/state/cart';
import { Breakpoint, useBreakpoint } from 'src/hooks/useBreakpoint';
import { CartItem } from 'src/core/state/cart';
import { EMPTY_IMAGE, itemImagesFromQuery } from 'src/components/Basket/utils/itemImagesFromQuery';
import { getCart_getCart } from 'src/queries/__generated__/getCart';

import { AppendBasketState as AppendStateBasket } from '../Basket/feature/basket.actions';

import { SummaryStatic } from './SummaryStatic';
import { SummaryPopup } from './SummaryPopup';

type Props = {
    summary: PriceSummary | null;
    products: AtbDetails[];
    step?: string;
};

export const Summary: React.FC<Props> = ({ summary, products, step }) => {
    const [breakpoint] = useBreakpoint();

    /**
     * NOTE: Previously wrapped in `<StaticRouter>`, but `react-router` v7 strictly
     * forbids nesting a `<Router>` inside another `<Router>` (the app already
     * renders `<HistoryRouter>` at the root). That nested router caused
     *   "Error: You cannot render a <Router> inside another <Router>"
     * and crashed every /customisation/* route post-Phase-3 upgrade. The
     * children don't actually need their own router — they read the existing
     * one via `useLocation`/`<Link>`.
     */
    return (
        <>
            {breakpoint === Breakpoint.Desk && <SummaryStatic summary={summary} products={products} step={step} />}
            {breakpoint === Breakpoint.Mob && <SummaryPopup products={products} />}
        </>
    );
};

const selector = (s: AppendState<CartAppendState<AppendStateBasket>>) => {
    return {
        summary: s.customisation.summary,
        cartItems: s.cart.items,
        raw: s.cart.raw,
        itemIds: s.customisation.item_ids,
        step: s.customisation.step,
    };
};
export const SummaryConnected = React.memo(() => {
    const { summary, cartItems, itemIds, raw, step } = useSelector(selector, shallowEqual);
    const matchingCartItems = cartItems.filter((item) => itemIds.indexOf(Number(item.id)) > -1);

    /**
     * Collect the cart images for the customisation section
     */
    const images = useMemo(() => itemImagesFromQuery(raw as getCart_getCart), [raw]);

    const products: AtbDetails[] = matchingCartItems.map((item: CartItem) => {
        const labels = (item.__typename === 'ConfigurableCartItem' &&
            item.configurable_options.map((opt) => opt.value_label)) || ['Qty'];
        return {
            items: [{ labels, qty: item.quantity }],
            name: item.product.name,
            qty: item.quantity,
            image: images.get(item.id)?.url || EMPTY_IMAGE.url,
            sku: item.product.sku,
            itemPrice: 0,
        } as AtbDetails;
    });
    return <Summary summary={summary} products={products} step={step} />;
});
